import React from 'react';
import {
  FaCheckCircle,
  FaClock,
  FaBuilding,
  FaQuestionCircle,
  FaExclamationTriangle,
  FaBan,
  FaRegCircle,
} from 'react-icons/fa';
import './PaymentStatusBadge.css';

/**
 * Mapeo único de estado de pago -> icono/texto/color (Parte 6). Se reutiliza
 * en la tarjeta de encuesta, el detalle, el resumen de perfiles, la gestión
 * administrativa y el historial de pagos para no duplicar etiquetas ni
 * estilos en varios archivos.
 *
 * IMPORTANTE sobre qué estados son "reales": 'paid', 'free', 'pending_manual',
 * 'pending_online' y 'unpaid' son los únicos valores que el backend persiste
 * en la columna `paymentStatus` de cada inscripción (ver
 * falla-plaza-rodrigo-api/index.js). 'failed' y 'cancelled' son estados
 * transitorios de una operación de Redsys en curso: si un pago falla o se
 * cancela, el registro de inscripción vuelve a 'unpaid', nunca se guarda
 * como 'failed'/'cancelled'. Se soportan aquí igualmente, por robustez y
 * por si en el futuro se llegan a persistir, pero hoy no aparecerán en la
 * gestión administrativa ni en el historial.
 */
const STATUS_META = {
  paid: {
    label: 'Pago online confirmado',
    shortLabel: 'Pago confirmado',
    icon: FaCheckCircle,
    className: 'psb-paid',
  },
  free: {
    label: 'Sin pago necesario',
    shortLabel: 'Gratis',
    icon: FaCheckCircle,
    className: 'psb-free',
  },
  pending_online: {
    label: 'Pendiente de pago online',
    shortLabel: 'Pendiente de pago',
    icon: FaClock,
    className: 'psb-pending',
  },
  pending_manual: {
    label: 'Pagarás en el casal',
    shortLabel: 'Pago en el casal',
    icon: FaBuilding,
    className: 'psb-manual',
  },
  unpaid: {
    label: 'Forma de pago sin decidir',
    shortLabel: 'Sin decidir',
    icon: FaQuestionCircle,
    className: 'psb-unknown',
  },
  failed: {
    label: 'Pago no completado',
    shortLabel: 'Pago fallido',
    icon: FaExclamationTriangle,
    className: 'psb-failed',
  },
  cancelled: {
    label: 'Pago cancelado',
    shortLabel: 'Cancelado',
    icon: FaBan,
    className: 'psb-cancelled',
  },
  none: {
    label: 'Todavía no te has inscrito',
    shortLabel: 'No inscrito',
    icon: FaRegCircle,
    className: 'psb-none',
  },
  registered: {
    label: 'Te has apuntado',
    shortLabel: 'Apuntado',
    icon: FaCheckCircle,
    className: 'psb-registered',
  },
};

export const getPaymentStatusMeta = (status) => STATUS_META[status] || STATUS_META.unpaid;

/**
 * @param {string} status - paid | free | pending_online | pending_manual | unpaid | failed | cancelled | registered | none
 * @param {string} [method] - 'bizum' | 'online' | 'manual' | 'free' (ajusta el texto cuando status es 'paid')
 * @param {boolean} [compact] - usa la etiqueta corta (filas de tabla, chips)
 */
export const PaymentStatusBadge = ({ status, method, compact = false, className = '' }) => {
  const meta = getPaymentStatusMeta(status);
  const Icon = meta.icon;
  let label = compact ? meta.shortLabel : meta.label;

  if (status === 'paid' && method === 'bizum') {
    label = compact ? 'Pagado (Bizum)' : 'Pago con Bizum confirmado';
  }

  return (
    <span className={`payment-status-badge ${meta.className} ${className}`.trim()}>
      <Icon className="payment-status-badge-icon" aria-hidden="true" />
      <span className="payment-status-badge-text">{label}</span>
    </span>
  );
};

export default PaymentStatusBadge;
